import { nodeValueOrder } from "./nodeValueOrder";
import {
  GeneratedLayout,
  GeneratedPlacement,
  GeneratedSegment,
  Orientation,
  PATH_SEPARATOR,
  SpineLevel,
  StudioDraft,
  StudioItem,
  StudioRunGeometry,
  effectiveAttrs,
  levelsForScope,
  slabPathOfAttrs,
} from "./studioTypes";

interface WalkEntry {
  item: StudioItem;
  attrs: Record<string, string | null>;
  band: readonly number[];

  hFacings?: number;
}

type Openings = ReadonlyMap<number, number>;

class LaneSim {
  private readonly fill = new Map<string, number>();
  get(key: string): number {
    return this.fill.get(key) ?? 0;
  }
  add(key: string, widthCm: number): void {
    this.fill.set(key, this.get(key) + widthCm);
  }
}

const facingWidth = (entry: { item: StudioItem }) => entry.item.facingWidthCm * entry.item.facings;

const groupBy = (
  entries: readonly { item: StudioItem; attrs: Record<string, string | null> }[],
  attr: string,
) => {
  const groups = new Map<
    string | null,
    { item: StudioItem; attrs: Record<string, string | null> }[]
  >();
  for (const entry of entries) {
    const value = entry.attrs[attr] ?? null;
    const group = groups.get(value);
    if (group) group.push(entry);
    else groups.set(value, [entry]);
  }
  return groups;
};

const orderedKeys = (groups: Map<string | null, unknown>, want: readonly string[] | undefined) => {
  const keys = [...groups.keys()];
  if (!want || want.length === 0) return keys;
  const wanted = want.filter((value) => groups.has(value));
  const rest = keys.filter((value) => value === null || !want.includes(value));
  return [...wanted, ...rest];
};

export const shelfSplit = (groupWidths: readonly number[], shelves: number): number[] => {
  const total = groupWidths.reduce((sum, width) => sum + width, 0) || 1;
  const raw = groupWidths.map((width) => (width / total) * shelves);
  const base = raw.map((share) => Math.max(1, Math.floor(share)));
  let left = shelves - base.reduce((sum, count) => sum + count, 0);
  const remainders = raw
    .map((share, index) => ({ index, fraction: share - Math.floor(share) }))
    .sort((a, b) => b.fraction - a.fraction);
  let cursor = 0;
  while (left > 0 && remainders.length > 0) {
    base[remainders[cursor % remainders.length].index] += 1;
    left -= 1;
    cursor += 1;
  }
  while (left < 0) {
    const biggest = base.indexOf(Math.max(...base));
    if (base[biggest] <= 1) break;
    base[biggest] -= 1;
    left += 1;
  }
  return base;
};

const orientationAt = (
  draft: Pick<StudioDraft, "orientOverrides">,
  level: SpineLevel,
  path: readonly string[],
): Orientation => draft.orientOverrides[path.join(PATH_SEPARATOR)] ?? level.orient;

const hasVerticalSplitBelow = (
  entries: readonly { item: StudioItem; attrs: Record<string, string | null> }[],
  levelIndex: number,
  draft: StudioDraft,
  path: readonly string[],
): boolean => {
  const levels = levelsForScope(draft.spine, draft.scopedLevelOrder, path);
  if (levelIndex >= levels.length || entries.length <= 1) return false;
  const level = levels[levelIndex];
  const groups = groupBy(entries, level.attr);
  if (groups.size < 2) return hasVerticalSplitBelow(entries, levelIndex + 1, draft, path);
  if (orientationAt(draft, level, path) === "vertical") return true;
  const keys = orderedKeys(groups, draft.valueOrder[level.attr]);
  return keys.some((key, index) =>
    hasVerticalSplitBelow(groups.get(key) ?? [], levelIndex + 1, draft, [
      ...path,
      keys[index] ?? "",
    ]),
  );
};

const emitRows = (
  entries: { item: StudioItem; attrs: Record<string, string | null> }[],
  levelIndex: number,
  draft: StudioDraft,
  band: readonly number[],
  path: readonly string[],
  out: WalkEntry[],
  sim: LaneSim,
  openings: Openings,
) => {
  const ordered: WalkEntry[] = [];
  orderSubtree(entries, levelIndex, draft, path, ordered, band);
  const laneKey = (rank: number, entry: WalkEntry) => `${entry.item.run}|${rank}`;
  const widthOf = (entry: WalkEntry) =>
    entry.item.facingWidthCm * (entry.hFacings ?? entry.item.facings);

  const hintTotalOf = (entry: WalkEntry): number | undefined => {
    const hints = draft.shelfHints[entry.item.skuId];
    if (!hints || hints.length === 0) return undefined;
    return hints.reduce((sum, hint) => sum + hint.hFacings, 0);
  };
  if (band.length <= 1 || ordered.length <= 1) {
    for (const entry of ordered) {
      const hints = draft.shelfHints[entry.item.skuId];

      if (hints && hints.length >= 1 && hints.every((hint) => band.includes(hint.shelf))) {
        for (const hint of hints) {
          out.push({ ...entry, band: [hint.shelf], hFacings: hint.hFacings });
          sim.add(laneKey(hint.shelf, entry), hint.hFacings * entry.item.facingWidthCm);
        }
      } else {
        const poured = { ...entry, hFacings: entry.hFacings ?? hintTotalOf(entry) };
        out.push(poured);
        sim.add(laneKey(band[0], entry), widthOf(poured));
      }
    }
    return;
  }

  const isHinted = (entry: WalkEntry) => {
    const hints = draft.shelfHints[entry.item.skuId];
    return Boolean(hints && hints.every((hint) => band.includes(hint.shelf)));
  };
  const free = ordered.filter((entry) => !isHinted(entry));
  const freeRow = new Map<WalkEntry, number>();
  const freshMode = Object.keys(draft.shelfHints).length === 0;
  if (free.length > 0) {
    const totalWidth = free.reduce((sum, entry) => sum + widthOf(entry), 0);

    const dealShare = totalWidth / band.length;
    const rows: WalkEntry[][] = band.map(() => []);
    const fits = (rank: number, entry: WalkEntry) =>
      (openings.get(rank) ?? Number.POSITIVE_INFINITY) + 0.5 >= (entry.item.heightCm || 0);
    const floorShare = free.reduce((sum, entry) => sum + entry.item.facingWidthCm, 0) / band.length;

    const run0 = free[0].item.run;
    let startRow = 0;
    if (freshMode) {
      const totalFree = free.reduce((sum, entry) => sum + widthOf(entry), 0);
      if (totalFree < dealShare * 1.5) {
        const fills = band.map((rank) => sim.get(`${run0}|${rank}`));
        startRow = fills.indexOf(Math.min(...fills));
      }
    }
    let row = startRow;
    let rowWidth = 0;
    let rowFloor = 0;
    for (const entry of free) {
      const width = widthOf(entry);

      const floorStep = entry.item.facingWidthCm;
      if (
        (rowWidth + width / 2 > dealShare ||
          (freshMode && rowFloor + floorStep / 2 > floorShare)) &&
        row < band.length - 1
      ) {
        row += 1;
        rowWidth = 0;
        rowFloor = 0;
      }

      let landed = row;
      if (!fits(band[landed], entry)) {
        const candidates = band
          .map((rank, index) => index)
          .filter((index) => fits(band[index], entry))
          .sort((a, b) => Math.abs(a - row) - Math.abs(b - row));
        if (candidates.length > 0) landed = candidates[0];
      }
      if (landed === row) {
        rowWidth += width;
        rowFloor += entry.item.facingWidthCm;
      }
      freeRow.set(entry, landed);
      rows[landed].push(entry);
    }
  }
  for (const entry of ordered) {
    if (isHinted(entry)) {
      for (const hint of draft.shelfHints[entry.item.skuId]) {
        out.push({ ...entry, band: [hint.shelf], hFacings: hint.hFacings });
        sim.add(laneKey(hint.shelf, entry), hint.hFacings * entry.item.facingWidthCm);
      }
    } else {
      const row = band[freeRow.get(entry) ?? 0];
      const fallback = [...band]
        .filter((rank) => rank !== row)
        .sort((a, b) => Math.abs(a - row) - Math.abs(b - row));
      const poured = {
        ...entry,
        band: [row, ...fallback],
        hFacings: entry.hFacings ?? hintTotalOf(entry),
      };
      out.push(poured);
      sim.add(laneKey(row, entry), widthOf(poured));
    }
  }
};

const orderSubtree = (
  entries: { item: StudioItem; attrs: Record<string, string | null> }[],
  levelIndex: number,
  draft: StudioDraft,
  path: readonly string[],
  out: WalkEntry[],
  band: readonly number[],
) => {
  const levels = levelsForScope(draft.spine, draft.scopedLevelOrder, path);
  if (levelIndex >= levels.length || entries.length <= 1) {
    for (const entry of entries) out.push({ ...entry, band });
    return;
  }
  const level = levels[levelIndex];
  const groups = groupBy(entries, level.attr);
  if (groups.size < 2) {
    orderSubtree(entries, levelIndex + 1, draft, path, out, band);
    return;
  }
  const pathKey = path.join(PATH_SEPARATOR);
  const keys = orderedKeys(groups, nodeOrderOf(entries, draft, pathKey, level.attr));
  keys.forEach((key) => {
    orderSubtree(groups.get(key) ?? [], levelIndex + 1, draft, [...path, key ?? ""], out, band);
  });
};

const nodeOrderOf = (
  entries: readonly { item: StudioItem; attrs: Record<string, string | null> }[],
  draft: StudioDraft,
  pathKey: string,
  attr: string,
): readonly string[] =>
  nodeValueOrder(
    draft,
    [pathKey],
    attr,
    entries.map((entry) => ({ value: entry.attrs[attr], placements: entry.item.placements })),
  );

const walk = (
  entries: { item: StudioItem; attrs: Record<string, string | null> }[],
  levelIndex: number,
  draft: StudioDraft,
  band: readonly number[],
  path: readonly string[],
  out: WalkEntry[],
  sim: LaneSim,
  openings: Openings,
) => {
  const levels = levelsForScope(draft.spine, draft.scopedLevelOrder, path);
  if (
    levelIndex >= levels.length ||
    entries.length <= 1 ||
    (band.length > 1 && !hasVerticalSplitBelow(entries, levelIndex, draft, path))
  ) {
    emitRows(entries, levelIndex, draft, band, path, out, sim, openings);
    return;
  }
  const level = levels[levelIndex];
  const groups = groupBy(entries, level.attr);
  if (groups.size < 2) {
    walk(entries, levelIndex + 1, draft, band, path, out, sim, openings);
    return;
  }
  const pathKey = path.join(PATH_SEPARATOR);
  const keys = orderedKeys(groups, nodeOrderOf(entries, draft, pathKey, level.attr));
  const grouped = keys.map((key) => groups.get(key) ?? []);

  if (orientationAt(draft, level, path) === "vertical" && band.length > 1) {
    const childPaths = keys.map((key) => [...path, key ?? ""]);
    const overrideBands = childPaths.map((childPath) => {
      const override = draft.bandOverrides[childPath.join(PATH_SEPARATOR)];
      if (!override) return null;
      const within = override.filter((rank) => band.includes(rank));
      return within.length > 0 ? within : null;
    });
    const free = grouped
      .map((group, index) => ({ group, index }))
      .filter(({ index }) => overrideBands[index] === null);
    if (free.length > 0) {
      const widths = free.map(({ group }) =>
        group.reduce((sum, entry) => sum + facingWidth(entry), 0),
      );
      if (Object.keys(draft.shelfHints).length === 0) {
        const floors = free.map(({ group }) =>
          group.reduce((sum, entry) => sum + entry.item.facingWidthCm, 0),
        );
        floors.forEach((floor, index) => {
          widths[index] = floor;
        });
      }

      const freshClusters = Object.keys(draft.shelfHints).length === 0;
      const clusterOf: number[] = [];
      const clusterWeights: number[] = [];
      if (freshClusters) {
        const parentShare =
          widths.reduce((sum, width) => sum + width, 0) / Math.max(1, band.length);
        widths.forEach((width) => {
          const worthy = width >= parentShare * 0.75;
          if (worthy || clusterWeights.length === 0) {
            clusterWeights.push(width);
            clusterOf.push(clusterWeights.length - 1);
          } else {
            clusterWeights[clusterWeights.length - 1] += width;
            clusterOf.push(clusterWeights.length - 1);
          }
        });
      } else {
        widths.forEach((width, position) => {
          clusterWeights.push(width);
          clusterOf.push(position);
        });
      }
      const split = shelfSplit(clusterWeights, band.length);
      const clusterBands: number[][] = [];
      {
        let offset = 0;
        split.forEach((count) => {
          const take = band.slice(offset, offset + count);
          offset += count;
          clusterBands.push(take.length > 0 ? [...take] : [band[band.length - 1]]);
        });
      }
      free.forEach(({ index }, position) => {
        overrideBands[index] = clusterBands[clusterOf[position]];
      });
    }
    grouped.forEach((group, index) => {
      walk(
        group,
        levelIndex + 1,
        draft,
        overrideBands[index] ?? band,
        childPaths[index],
        out,
        sim,
        openings,
      );
    });
    return;
  }

  const flush = band.length > 1 && Object.keys(draft.shelfHints).length === 0;
  if (flush) {
    const widths = grouped.map((group) =>
      group.reduce((sum, entry) => sum + facingWidth(entry), 0),
    );
    const facings = entries.map((entry) => entry.item.facingWidthCm).sort((a, b) => a - b);
    const medianFacing = facings[Math.floor((facings.length - 1) / 2)] ?? 0;
    const minColumn = band.length * medianFacing * 2;
    interface PoolChild {
      index: number;
    }
    const pools: PoolChild[][] = [];
    grouped.forEach((group, index) => {
      const small = widths[index] < minColumn;
      const last = pools[pools.length - 1];
      const lastSmall = last && last.length > 0 && widths[last[last.length - 1].index] < minColumn;
      if (small && lastSmall) last.push({ index });
      else pools.push([{ index }]);
    });
    for (const pool of pools) {
      if (pool.length === 1) {
        const index = pool[0].index;
        walk(
          grouped[index],
          levelIndex + 1,
          draft,
          band,
          [...path, keys[index] ?? ""],
          out,
          sim,
          openings,
        );
        continue;
      }

      const weights = pool.map((child) => widths[child.index]);
      const poolTotal = weights.reduce((sum, weight) => sum + weight, 0);
      const clusterOf: number[] = [];
      const clusterWeights: number[] = [];
      const memberShare = poolTotal / Math.max(1, band.length);
      weights.forEach((weight) => {
        const worthy = weight >= memberShare * 0.75;
        if (worthy || clusterWeights.length === 0) {
          clusterWeights.push(weight);
          clusterOf.push(clusterWeights.length - 1);
        } else {
          clusterWeights[clusterWeights.length - 1] += weight;
          clusterOf.push(clusterWeights.length - 1);
        }
      });
      const split = shelfSplit(clusterWeights, band.length);
      const clusterBands: number[][] = [];
      let offset = 0;
      split.forEach((count) => {
        const take = band.slice(offset, offset + count);
        offset += count;
        clusterBands.push(take.length > 0 ? [...take] : [band[band.length - 1]]);
      });
      pool.forEach((child, position) => {
        const subBand = clusterBands[clusterOf[position]];
        walk(
          grouped[child.index],
          levelIndex + 1,
          draft,
          subBand,
          [...path, keys[child.index] ?? ""],
          out,
          sim,
          openings,
        );
      });
    }
    return;
  }
  grouped.forEach((group, index) => {
    walk(group, levelIndex + 1, draft, band, [...path, keys[index] ?? ""], out, sim, openings);
  });
};

interface Lane {
  openingCm?: number;
  run: number;
  shelf: number;
  xCm: number;
  widthCm: number;
}

const MAX_SHED_FACINGS = 1;

const pack = (tokens: readonly WalkEntry[], runs: readonly StudioRunGeometry[]) => {
  const lanes: Lane[] = [];
  const laneIndex = new Map<string, Lane>();
  for (const run of runs) {
    const byRank = [...run.shelves].sort((a, b) => a.rank - b.rank);
    for (const shelf of byRank) {
      const above = byRank.find((candidate) => candidate.rank === shelf.rank - 1);
      const ceiling = above ? above.yCm - above.thicknessCm : run.heightCm || Infinity;
      const openingCm = Math.max(ceiling - shelf.yCm, 0) || Infinity;
      const lane: Lane = {
        run: run.run,
        shelf: shelf.rank,
        xCm: 0,
        widthCm: run.widthCm,
        openingCm,
      };
      lanes.push(lane);
      laneIndex.set(`${run.run}|${shelf.rank}`, lane);
    }
  }
  const placements: Record<string, GeneratedPlacement> = {};
  const ceilingViolations: {
    skuId: string;
    run: number;
    shelf: number;
    heightCm: number;
    openingCm: number;
  }[] = [];
  const shedFacings: {
    skuId: string;
    run: number;
    shelf: number;
    requested: number;
    placed: number;
  }[] = [];

  for (const token of tokens) {
    const entry = token;
    const left = entry.hFacings ?? entry.item.facings;
    const width = entry.item.facingWidthCm;

    // Stay on this SKU's run: band is shelf ranks, not aisles.
    const ownRun = entry.item.run;
    const preferred: Lane[] = [];
    for (const rank of entry.band) {
      const lane = laneIndex.get(`${ownRun}|${rank}`);
      if (lane) preferred.push(lane);
    }
    const bandCenter =
      entry.band.length > 0
        ? entry.band.reduce((sum, rank) => sum + rank, 0) / entry.band.length
        : 0;
    const rest = lanes
      .filter((lane) => lane.run === ownRun && !preferred.includes(lane))
      .sort((a, b) => Math.abs(a.shelf - bandCenter) - Math.abs(b.shelf - bandCenter));

    const heightCm = entry.item.heightCm || 0;
    const fitsHeight = (lane: Lane) => (lane.openingCm ?? Infinity) + 0.5 >= heightCm;
    const chainAll = [...preferred, ...rest];
    let chain = chainAll.filter(fitsHeight);
    if (chain.length === 0) {
      const tallest = chainAll.reduce((a, b) =>
        (a.openingCm ?? Infinity) >= (b.openingCm ?? Infinity) ? a : b,
      );
      ceilingViolations.push({
        skuId: entry.item.skuId,
        run: tallest.run,
        shelf: tallest.shelf,
        heightCm,
        openingCm: tallest.openingCm ?? 0,
      });
      chain = [tallest];
    }

    const fitsIn = (lane: Lane) =>
      Math.max(0, Math.floor((lane.widthCm - lane.xCm + 0.01) / width));
    const segments: GeneratedSegment[] = [];
    const placeWhole = (lane: Lane, count: number) => {
      segments.push({
        run: lane.run,
        shelf: lane.shelf,
        xCm: +lane.xCm.toFixed(1),
        hFacings: count,
      });
      lane.xCm += count * width;
    };

    const bandLanes = chain.filter((lane) => preferred.includes(lane));
    if (chain.length > 0) {
      const chosen = chain[0];
      const fitsChosen = fitsIn(chosen);
      if (fitsChosen >= left) {
        placeWhole(chosen, left);
      } else if (fitsChosen >= 1 && left - fitsChosen <= MAX_SHED_FACINGS) {
        shedFacings.push({
          skuId: entry.item.skuId,
          run: chosen.run,
          shelf: chosen.shelf,
          requested: left,
          placed: fitsChosen,
        });
        placeWhole(chosen, fitsChosen);
      } else {
        const nextRow = (bandLanes.length > 0 ? bandLanes : [chosen]).find(
          (lane) => fitsIn(lane) >= left,
        );
        if (nextRow) {
          placeWhole(nextRow, left);
        } else {
          const rows = bandLanes.length > 0 ? bandLanes : [chosen];
          const roomiest = rows.reduce((a, b) => (fitsIn(b) > fitsIn(a) ? b : a));
          const roomFits = fitsIn(roomiest);
          if (roomFits >= 1) {
            shedFacings.push({
              skuId: entry.item.skuId,
              run: roomiest.run,
              shelf: roomiest.shelf,
              requested: left,
              placed: roomFits,
            });
            placeWhole(roomiest, roomFits);
          } else {
            placeWhole(chosen, left);
          }
        }
      }
    } else {
      const ownLanes = lanes.filter((lane) => lane.run === ownRun);
      const emptiest = (ownLanes.length > 0 ? ownLanes : lanes).reduce((a, b) =>
        a.xCm <= b.xCm ? a : b,
      );
      placeWhole(emptiest, left);
    }
    const existing = placements[entry.item.skuId];
    placements[entry.item.skuId] = existing
      ? { skuId: entry.item.skuId, segments: [...existing.segments, ...segments] }
      : { skuId: entry.item.skuId, segments };
  }
  return { placements, ceilingViolations, shedFacings };
};

export type SquareBindReason = "floor" | "cap" | "band" | "height";

export interface FlushSquareOptions {
  keepShelvesFull?: boolean;

  fillToCapacity?: boolean;

  residualReasons?: Map<string, SquareBindReason>;
}

export const flushSquare = (
  layout: { placements: Record<string, GeneratedPlacement>; walkOrder: readonly string[] },
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
  options?: FlushSquareOptions,
): void => {
  const keepFull = options?.keepShelvesFull === true;
  const bySku = new Map(items.map((item) => [item.skuId, item]));

  const slabOf = new Map<string, { key: string; bandValue: string }>();
  const slabFor = (item: StudioItem): { key: string; bandValue: string } => {
    const cached = slabOf.get(item.skuId);
    if (cached) return cached;
    const attrs = effectiveAttrs(item, draft);
    const { values, bandAttr } = slabPathOfAttrs(draft.spine, draft.scopedLevelOrder, attrs);
    const computed = {
      key: values.join(PATH_SEPARATOR),
      bandValue: bandAttr ? String(attrs[bandAttr] ?? "") : "",
    };
    slabOf.set(item.skuId, computed);
    return computed;
  };
  const keyOf = (item: StudioItem): string => slabFor(item).key;
  interface Seg {
    seg: GeneratedSegment;
    widthCm: number;
    heightCm: number;
    key: string;
    dos: number;
    maxFacings: number;
  }
  const lanes = new Map<string, Seg[]>();
  for (const placement of Object.values(layout.placements)) {
    const item = bySku.get(placement.skuId);
    if (!item) continue;
    for (const seg of placement.segments) {
      const laneKey = `${seg.run}|${seg.shelf}`;
      const lane = lanes.get(laneKey) ?? [];
      lane.push({
        seg,
        widthCm: item.facingWidthCm,
        heightCm: item.heightCm || 0,
        key: keyOf(item),
        dos: item.dosPerFacing ?? 0,
        maxFacings: item.maxFacings ?? Number.POSITIVE_INFINITY,
      });
      lanes.set(laneKey, lane);
    }
  }

  const laneFillOf = (lane: readonly Seg[]): number =>
    lane.reduce((sum, entry) => sum + entry.widthCm * entry.seg.hFacings, 0);
  const beforeFill = new Map<string, number>();
  lanes.forEach((lane, laneKey) => beforeFill.set(laneKey, laneFillOf(lane)));
  const blockOrder: string[] = [];
  const seen = new Set<string>();
  for (const id of layout.walkOrder) {
    const item = bySku.get(id);
    if (!item) continue;
    const key = keyOf(item);
    if (!seen.has(key)) {
      seen.add(key);
      blockOrder.push(key);
    }
  }

  const bandValue = (skuIdOf: string): string => {
    const item = bySku.get(skuIdOf);
    return item ? slabFor(item).bandValue : "";
  };
  const segSku = new Map<Seg, string>();
  for (const placement of Object.values(layout.placements)) {
    for (const [laneKey, lane] of lanes) {
      void laneKey;
      for (const entry of lane) {
        if (placement.segments.includes(entry.seg)) segSku.set(entry, placement.skuId);
      }
    }
  }

  const rankOf = new Map<string, number>(layout.walkOrder.map((id, index) => [id, index]));
  const blockRank = new Map<string, number>(blockOrder.map((key, index) => [key, index]));
  const laneSort = (lane: Seg[]) =>
    lane.sort(
      (a, b) =>
        (blockRank.get(a.key) ?? 0) - (blockRank.get(b.key) ?? 0) ||
        (rankOf.get(segSku.get(a) ?? "") ?? 0) - (rankOf.get(segSku.get(b) ?? "") ?? 0),
    );
  lanes.forEach((lane) => laneSort(lane));

  for (const key of keepFull ? [] : blockOrder) {
    for (let guard = 0; guard < 200; guard++) {
      const perLane: { laneKey: string; segs: Seg[]; floor: number }[] = [];
      lanes.forEach((lane, laneKey) => {
        const segs = lane.filter((entry) => entry.key === key);
        if (segs.length > 0)
          perLane.push({
            laneKey,
            segs,
            floor: segs.reduce((sum, entry) => sum + entry.widthCm, 0),
          });
      });
      if (perLane.length < 2) break;
      perLane.sort((a, b) => Number(a.laneKey.split("|")[1]) - Number(b.laneKey.split("|")[1]));
      let moved = false;
      for (let i = 0; i < perLane.length - 1 && !moved; i++) {
        const a = perLane[i];
        const b = perLane[i + 1];
        const heavy = a.floor >= b.floor ? a : b;
        const light = a.floor >= b.floor ? b : a;
        if (heavy.segs.length < 2) continue;

        const movingDown = heavy === a;
        const moving = movingDown ? heavy.segs[heavy.segs.length - 1] : heavy.segs[0];
        if (heavy.floor - light.floor <= moving.widthCm) continue;
        const movingValue = bandValue(segSku.get(moving) ?? "");
        if (!light.segs.some((entry) => bandValue(segSku.get(entry) ?? "") === movingValue))
          continue;
        const srcLane = lanes.get(heavy.laneKey) ?? [];
        srcLane.splice(srcLane.indexOf(moving), 1);
        const dstLane = lanes.get(light.laneKey) ?? [];
        moving.seg.shelf = Number(light.laneKey.split("|")[1]);
        dstLane.push(moving);
        laneSort(dstLane);
        moved = true;
      }
      if (!moved) break;
    }
  }

  const medians = new Map<string, number>();
  const laneFloors = new Map<string, Map<string, number>>();
  const laneKeysOf = new Map<string, string[]>();
  for (const key of blockOrder) {
    const widths: number[] = [];
    const mine: string[] = [];
    const flrs = new Map<string, number>();
    lanes.forEach((lane, laneKey) => {
      const segs = lane.filter((entry) => entry.key === key);
      if (segs.length === 0) return;
      mine.push(laneKey);
      widths.push(segs.reduce((sum, entry) => sum + entry.widthCm * entry.seg.hFacings, 0));
      flrs.set(
        laneKey,
        segs.reduce((sum, entry) => sum + entry.widthCm, 0),
      );
    });
    laneKeysOf.set(key, mine);
    laneFloors.set(key, flrs);
    const sorted = [...widths].sort((a, b) => a - b);
    medians.set(key, sorted[Math.floor((sorted.length - 1) / 2)] ?? 0);
  }
  const capacity = new Map(runs.map((run) => [run.run, run.widthCm]));

  if (keepFull) {
    const reasons = options?.residualReasons;
    const medianFacing = (() => {
      const widths: number[] = [];
      lanes.forEach((lane) => lane.forEach((entry) => widths.push(entry.widthCm)));
      const sorted = widths.sort((a, b) => a - b);
      return sorted[Math.floor((sorted.length - 1) / 2)] ?? 0;
    })();
    const capOf = (laneKey: string): number =>
      capacity.get(Number(laneKey.split("|")[0])) ?? Number.POSITIVE_INFINITY;
    const laneOpenings = new Map<string, number>();
    for (const run of runs) {
      const byRank = [...run.shelves].sort((a, b) => a.rank - b.rank);
      for (const shelf of byRank) {
        const above = byRank.find((candidate) => candidate.rank === shelf.rank - 1);
        const ceilingY = above ? above.yCm - above.thicknessCm : run.heightCm || Infinity;
        laneOpenings.set(`${run.run}|${shelf.rank}`, Math.max(ceilingY - shelf.yCm, 0) || Infinity);
      }
    }

    interface Slot {
      laneKey: string;
      segs: Seg[];
      anchor: number;
      width: () => number;
      airAfter: number;
    }
    const slotsOf = new Map<string, Slot[]>();
    const laneSlots = new Map<string, Slot[]>();
    lanes.forEach((lane, laneKey) => {
      const groups: { key: string; segs: Seg[] }[] = [];
      for (const entry of lane) {
        const last = groups[groups.length - 1];
        if (last && last.key === entry.key) last.segs.push(entry);
        else groups.push({ key: entry.key, segs: [entry] });
      }
      for (let index = 0; index < groups.length; index++) {
        const group = groups[index];
        const anchor = Math.min(...group.segs.map((entry) => entry.seg.xCm));
        const width = () =>
          group.segs.reduce((sum, entry) => sum + entry.widthCm * entry.seg.hFacings, 0);
        const next = groups[index + 1];
        const nextAnchor = next
          ? Math.min(...next.segs.map((entry) => entry.seg.xCm))
          : Math.max(capOf(laneKey), anchor + width());
        const slot: Slot = {
          laneKey,
          segs: group.segs,
          anchor,
          width,
          airAfter: Math.max(0, nextAnchor - (anchor + width())),
        };
        const list = slotsOf.get(group.key) ?? [];
        list.push(slot);
        slotsOf.set(group.key, list);
        const perLane = laneSlots.get(laneKey) ?? [];
        perLane.push(slot);
        laneSlots.set(laneKey, perLane);
      }
    });

    const recomputeAir = () => {
      laneSlots.forEach((perLane, laneKey) => {
        for (let index = 0; index < perLane.length; index++) {
          const slot = perLane[index];
          const end = slot.anchor + slot.width();
          const next = perLane[index + 1];
          const nextAnchor = next ? next.anchor : Math.max(capOf(laneKey), end);
          slot.airAfter = Math.max(0, nextAnchor - end);
        }
      });
    };

    const alignAnchors = () => {
      for (let round = 0; round < 3; round++) {
        for (const key of blockOrder) {
          const slots = slotsOf.get(key) ?? [];
          if (slots.length < 2) continue;

          const anchors = slots.map((slot) => slot.anchor).sort((a, b) => a - b);
          const targetAnchor = anchors[Math.ceil((anchors.length - 1) / 2)];
          for (const slot of slots) {
            const perLane = laneSlots.get(slot.laneKey) ?? [];
            const index = perLane.indexOf(slot);
            const prev = index > 0 ? perLane[index - 1] : null;
            const airBefore = prev ? prev.airAfter : slot.anchor;
            const shift = Math.min(Math.max(targetAnchor - slot.anchor, -airBefore), slot.airAfter);
            if (Math.abs(shift) < 0.05) continue;
            slot.anchor += shift;
            slot.airAfter -= shift;
            if (prev) prev.airAfter += shift;
          }
        }
      }
    };
    const tradeToward = (segs: Seg[], goal: number) => {
      const total = () => segs.reduce((sum, entry) => sum + entry.widthCm * entry.seg.hFacings, 0);
      for (let guard = 0; guard < 400; guard++) {
        const pick = segs
          .filter((entry) => entry.seg.hFacings > 1 && total() - entry.widthCm / 2 > goal)
          .sort((a, b) => b.dos * b.seg.hFacings - a.dos * a.seg.hFacings)[0];
        if (!pick) break;
        pick.seg.hFacings -= 1;
      }
      for (let guard = 0; guard < 400; guard++) {
        const pick = segs
          .filter(
            (entry) => entry.seg.hFacings < entry.maxFacings && total() + entry.widthCm / 2 < goal,
          )
          .sort((a, b) => a.dos * a.seg.hFacings - b.dos * b.seg.hFacings)[0];
        if (!pick) break;
        pick.seg.hFacings += 1;
      }
    };
    const movedSegs = new Set<Seg>();
    for (const key of blockOrder) {
      const slots = slotsOf.get(key) ?? [];
      if (slots.length < 2) continue;
      for (let round = 0; round < 8; round++) {
        const widths = slots.map((slot) => slot.width()).sort((a, b) => a - b);
        const median = widths[Math.ceil((widths.length - 1) / 2)];
        const maxFloor = Math.max(
          ...slots.map((slot) => slot.segs.reduce((sum, entry) => sum + entry.widthCm, 0)),
        );
        const minReach = Math.min(
          ...slots.map((slot) => slot.anchor + slot.width() + slot.airAfter - slot.anchor),
        );
        const goal = Math.min(Math.max(median, maxFloor), minReach);
        for (const slot of slots) tradeToward(slot.segs, goal);
        const spread =
          Math.max(...slots.map((slot) => slot.width())) -
          Math.min(...slots.map((slot) => slot.width()));
        if (spread <= medianFacing) break;

        const sorted = [...slots].sort((a, b) => a.width() - b.width());
        const lean = sorted[0];
        const wide = sorted[sorted.length - 1];
        const floorBound = wide.segs.every((entry) => entry.seg.hFacings <= 1);
        if (!floorBound || wide.segs.length < 2) {
          if (floorBound) reasons?.set(key, "floor");
          else reasons?.set(key, "cap");
          break;
        }
        const wideRank = Number(wide.laneKey.split("|")[1]);
        const leanRank = Number(lean.laneKey.split("|")[1]);
        const ordered = [...wide.segs].sort(
          (a, b) => (rankOf.get(segSku.get(a) ?? "") ?? 0) - (rankOf.get(segSku.get(b) ?? "") ?? 0),
        );
        const moving = leanRank > wideRank ? ordered[ordered.length - 1] : ordered[0];
        const movingWidth = moving.widthCm * moving.seg.hFacings;
        if (lean.airAfter < movingWidth) {
          reasons?.set(key, "cap");
          break;
        }
        const movingValue = bandValue(segSku.get(moving) ?? "");
        const destHasBand = lean.segs.some(
          (entry) => bandValue(segSku.get(entry) ?? "") === movingValue,
        );
        if (!destHasBand) {
          reasons?.set(key, "band");
          break;
        }
        if ((laneOpenings.get(lean.laneKey) ?? Infinity) + 0.5 < moving.heightCm) {
          reasons?.set(key, "height");
          break;
        }

        const srcLane = lanes.get(wide.laneKey) ?? [];
        srcLane.splice(srcLane.indexOf(moving), 1);
        wide.segs.splice(wide.segs.indexOf(moving), 1);
        wide.airAfter += movingWidth;
        const dstLane = lanes.get(lean.laneKey) ?? [];
        moving.seg.shelf = leanRank;
        moving.seg.run = Number(lean.laneKey.split("|")[0]);
        dstLane.push(moving);
        laneSort(dstLane);
        lean.segs.push(moving);
        lean.airAfter -= movingWidth;
        movedSegs.add(moving);
      }
      const finalSpread =
        Math.max(...slots.map((slot) => slot.width())) -
        Math.min(...slots.map((slot) => slot.width()));
      if (finalSpread > medianFacing && reasons && !reasons.has(key)) {
        reasons.set(key, "floor");
      }
    }

    recomputeAir();
    alignAnchors();

    lanes.forEach((lane, laneKey) => {
      const started = beforeFill.get(laneKey) ?? 0;
      const fillGoal = options?.fillToCapacity ? Math.max(started, capOf(laneKey)) : started;
      for (let guard = 0; guard < 400; guard++) {
        if (laneFillOf(lane) >= fillGoal - 0.5) break;
        const candidates: { entry: Seg; air: number }[] = [];
        slotsOf.forEach((slots) => {
          for (const slot of slots) {
            if (slot.laneKey !== laneKey) continue;
            for (const entry of slot.segs) {
              if (entry.seg.hFacings < entry.maxFacings && slot.airAfter >= entry.widthCm)
                candidates.push({ entry, air: slot.airAfter });
            }
          }
        });
        const pick = candidates.sort(
          (a, b) => a.entry.dos * a.entry.seg.hFacings - b.entry.dos * b.entry.seg.hFacings,
        )[0];
        if (!pick) break;
        pick.entry.seg.hFacings += 1;
        slotsOf.forEach((slots) => {
          for (const slot of slots) {
            if (slot.segs.includes(pick.entry)) slot.airAfter -= pick.entry.widthCm;
          }
        });
      }
    });

    for (const key of blockOrder) {
      const slots = slotsOf.get(key) ?? [];
      if (slots.length < 2) continue;
      const widths = slots.map((slot) => slot.width()).sort((a, b) => a - b);
      const top = widths[widths.length - 1];
      for (const slot of slots) {
        if (slot.width() >= top - 0.5) continue;
        const goal = Math.min(top, slot.width() + slot.airAfter);
        const before = slot.width();
        tradeToward(slot.segs, goal);
        slot.airAfter -= slot.width() - before;
      }
    }
    recomputeAir();
    alignAnchors();

    slotsOf.forEach((slots) => {
      for (const slot of slots) {
        let x = slot.anchor;
        const orderedSegs = [...slot.segs].sort(
          (a, b) => (rankOf.get(segSku.get(a) ?? "") ?? 0) - (rankOf.get(segSku.get(b) ?? "") ?? 0),
        );
        for (const entry of orderedSegs) {
          entry.seg.xCm = +x.toFixed(1);
          x += entry.widthCm * entry.seg.hFacings;
        }
      }
    });
    return;
  }

  let scale = 1;
  for (let pass = 0; pass < 8; pass++) {
    let worst = 1;
    lanes.forEach((_, laneKey) => {
      const runId = Number(laneKey.split("|")[0]);
      const cap = (capacity.get(runId) ?? Infinity) * 0.99;
      let sum = 0;
      for (const key of blockOrder) {
        if (!(laneKeysOf.get(key) ?? []).includes(laneKey)) continue;
        const floor = laneFloors.get(key)?.get(laneKey) ?? 0;
        const goal =
          (laneKeysOf.get(key) ?? []).length < 2
            ? Math.min(medians.get(key) ?? 0, Math.max(floor, (medians.get(key) ?? 0) * scale))
            : Math.max(floor, (medians.get(key) ?? 0) * scale);
        sum += goal;
      }
      if (Number.isFinite(cap) && sum > cap) worst = Math.max(worst, sum / cap);
    });
    if (worst <= 1.001) break;
    scale = scale / worst;
  }
  const goalOf = (key: string): number => (medians.get(key) ?? 0) * scale;

  const fitBlocks = (factor: number) => {
    for (const key of blockOrder) {
      if ((laneKeysOf.get(key) ?? []).length < 2) continue;
      const goal = goalOf(key) * factor;
      lanes.forEach((lane) => {
        const segs = lane.filter((entry) => entry.key === key);
        if (segs.length === 0) return;
        const total = () =>
          segs.reduce((sum, entry) => sum + entry.widthCm * entry.seg.hFacings, 0);
        for (let guard = 0; guard < 400; guard++) {
          const pick = segs
            .filter((entry) => entry.seg.hFacings > 1)
            .sort((a, b) => b.dos * b.seg.hFacings - a.dos * a.seg.hFacings)[0];
          if (!pick || total() <= goal + 0.5) break;
          pick.seg.hFacings -= 1;
        }
        for (let guard = 0; guard < 400; guard++) {
          const pick = segs
            .filter(
              (entry) =>
                entry.seg.hFacings < entry.maxFacings && total() + entry.widthCm <= goal + 0.5,
            )
            .sort((a, b) => a.dos * a.seg.hFacings - b.dos * b.seg.hFacings)[0];
          if (!pick) break;
          pick.seg.hFacings += 1;
        }
      });
    }
  };

  const outerOf = new Map<string, string>();
  for (const key of blockOrder) {
    outerOf.set(key, key.split(PATH_SEPARATOR).slice(0, 2).join(PATH_SEPARATOR));
  }
  const outerLanes = new Map<string, Set<string>>();
  for (const key of blockOrder) {
    const outer = outerOf.get(key) ?? "";
    const set = outerLanes.get(outer) ?? new Set<string>();
    for (const laneKey of laneKeysOf.get(key) ?? []) set.add(laneKey);
    outerLanes.set(outer, set);
  }

  const relayBlocks = () => {
    const cursor = new Map<string, number>();
    lanes.forEach((_, laneKey) => cursor.set(laneKey, 0));
    let currentOuter: string | null = null;
    for (const key of blockOrder) {
      const mine = laneKeysOf.get(key) ?? [];
      if (mine.length === 0) continue;
      const outer = outerOf.get(key) ?? "";
      if (outer !== currentOuter) {
        currentOuter = outer;
        const family = [...(outerLanes.get(outer) ?? [])];
        if (family.length > 1) {
          const edge = Math.max(...family.map((laneKey) => cursor.get(laneKey) ?? 0));
          for (const laneKey of family) cursor.set(laneKey, edge);
        }
      }

      const start = Math.max(...mine.map((laneKey) => cursor.get(laneKey) ?? 0));
      for (const laneKey of mine) {
        let x = Math.max(start, cursor.get(laneKey) ?? 0);
        for (const entry of lanes.get(laneKey) ?? []) {
          if (entry.key !== key) continue;
          entry.seg.xCm = +x.toFixed(1);
          x += entry.widthCm * entry.seg.hFacings;
        }
        cursor.set(laneKey, x);
      }
    }

    let worst = 1;
    lanes.forEach((_, laneKey) => {
      const runId = Number(laneKey.split("|")[0]);
      const cap = capacity.get(runId) ?? Infinity;
      const end = cursor.get(laneKey) ?? 0;
      if (Number.isFinite(cap) && end > cap) worst = Math.max(worst, end / cap);
    });
    return worst;
  };

  let factor = 1;
  for (let pass = 0; pass < 10; pass++) {
    fitBlocks(factor);
    const worst = relayBlocks();
    if (worst <= 1.005) break;
    factor = factor / worst;
  }
};

export const buildLayout = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  runs: readonly StudioRunGeometry[],
): GeneratedLayout => {
  const ranks = [...new Set(runs.flatMap((run) => run.shelves.map((shelf) => shelf.rank)))].sort(
    (a, b) => a - b,
  );
  const entries = items.map((item) => ({ item, attrs: effectiveAttrs(item, draft) }));
  const openings = new Map<number, number>();
  for (const run of runs) {
    const byRank = [...run.shelves].sort((a, b) => a.rank - b.rank);
    for (const shelf of byRank) {
      const above = byRank.find((candidate) => candidate.rank === shelf.rank - 1);
      const ceiling = above ? above.yCm - above.thicknessCm : run.heightCm || Infinity;
      const openingCm = Math.max(ceiling - shelf.yCm, 0) || Infinity;
      openings.set(shelf.rank, Math.min(openings.get(shelf.rank) ?? Infinity, openingCm));
    }
  }
  const order: WalkEntry[] = [];
  walk(entries, 0, draft, ranks, [], order, new LaneSim(), openings);
  const packed = pack(order, runs);
  const result = {
    placements: packed.placements,
    ceilingViolations: packed.ceilingViolations,
    shedFacings: packed.shedFacings,
    walkOrder: [...new Set(order.map((entry) => entry.item.skuId))],
  };

  // Hints mean reproduce the measured plan; squaring would move SKUs off those shelves.
  if (Object.keys(draft.shelfHints).length === 0) flushSquare(result, items, runs, draft);
  return result;
};

export const normativeDraftOf = (
  draft: StudioDraft,
  quantityAttribute: string = "size",
): StudioDraft => {
  const familyRank = (label: string): [number, number] => {
    const text = String(label);
    const magnitude = parseFloat(text.match(/-?\d+(?:\.\d+)?/)?.[0] ?? "0");
    const families: [RegExp, number, number][] = [
      [/fl\s*oz/i, 3, 1],
      [/\bml\b/i, 3, 0.033814],
      [/\bl\b/i, 3, 33.814],
      [/\blb\b|pound/i, 2, 16],
      [/\boz\b/i, 2, 1],
      [/\bg\b/i, 2, 0.035274],
      [/\bct\b|count|\bpk\b|pack/i, 0, 1],
      [/load/i, 1, 1],
    ];
    for (const [pattern, family, mult] of families) {
      if (pattern.test(text)) return [family, magnitude * mult];
    }
    return [4, magnitude];
  };
  const sizeAttrs = draft.spine
    .filter((level) => level.attr === quantityAttribute)
    .map((level) => level.attr);
  const valueOrder: Record<string, readonly string[]> = { ...draft.valueOrder };
  for (const attr of sizeAttrs) {
    valueOrder[attr] = [...(draft.valueOrder[attr] ?? [])].sort((a, b) => {
      const ka = familyRank(a);
      const kb = familyRank(b);
      return ka[0] - kb[0] || ka[1] - kb[1];
    });
  }

  // Drop measured maps so the generator runs in fresh mode.
  return {
    ...draft,
    valueOrder,
    scopedValueOrder: {},
    bandOverrides: {},
    orientOverrides: {},
    shelfHints: {},
  };
};

export const buildAsBuiltLayout = (items: readonly StudioItem[]): GeneratedLayout => {
  const placements: Record<string, GeneratedPlacement> = {};
  for (const item of items) {
    placements[item.skuId] = {
      skuId: item.skuId,
      segments: item.placements.map((placement) => ({
        run: placement.run,
        shelf: placement.shelf,
        xCm: placement.xCm,
        hFacings: placement.hFacings,
      })),
    };
  }
  const walkOrder = [...items]
    .sort((a, b) => {
      const pa = a.placements[0];
      const pb = b.placements[0];
      return pa.run - pb.run || pa.shelf - pb.shelf || pa.xCm - pb.xCm;
    })
    .map((item) => item.skuId);
  return { placements, walkOrder };
};
